import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Monitor, Smartphone, Undo2, AlertCircle, Loader2, X, Info,
} from 'lucide-react';
import { api } from '../../../lib/api';
import {
  targetLabel, verdictDetails, describeReasons, fixAdvice, describeBlocked, softWarnings, checkStatus,
  reviewDisabledReason, publishDisabledReason, pluralize, imageFileName, NO_RETRY_REASONS,
} from './inlineEdit';
import { isImageTarget } from './useInlineEditBridge';
import useInlinePublish, { POLL_MS } from './useInlinePublish';
import InlineImageForm from './InlineImageForm';
import { absoluteSrc, attachmentUrl, NewDescription, PictureSwap } from './InlineChangeSummary';
import {
  StageShell, ReviewStage, ProgressStage, SuccessStage, UnderReviewStage, FailedStage, focusRing, primaryBtn,
} from './InlinePublishStages';

export { POLL_MS };

// Right-hand panel of the Visual Editor while "Edit text & images" is on
// (docs/inline-edit-lab.md §8, §S2.3, and the school-side trial feedback).
// Publishing has its own flow (useInlinePublish): EDIT (the page picker and
// pending changes) → REVIEW ("Publish N changes to your website?", each change
// checked on phones automatically) → PROGRESS (the workflow's steps) →
// SUCCESS / UNDER REVIEW / FAILED. Everything talks to the page through
// `bridge` (useInlineEditBridge) and to the API through /api/edit-requests.
//
//   initialStatus  GET /api/edit-requests/inline-status, when the page already
//                  has it; otherwise the panel asks on mount. A publish still
//                  running opens straight into PROGRESS.

const labelClasses = 'block text-[10px] font-medium text-gray-500 mb-1.5 tracking-wider uppercase';
const inputClasses =
  'w-full px-3.5 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 focus:bg-white/[0.05] transition-all';

export default function InlineEditPanel({ bridge, siteOrigin, onOpenPage, onBusyChange, initialStatus = null }) {
  const entries = bridge.changeList;
  const [status, setStatus] = useState(initialStatus);
  const [uploading, setUploading] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  // Reload persistence: is a publish (or an undo) of this school's still running?
  useEffect(() => {
    if (initialStatus) return undefined;
    let alive = true;
    Promise.resolve(api.get('/api/edit-requests/inline-status'))
      .then((d) => { if (alive && d && typeof d === 'object') setStatus(d); })
      .catch(() => { /* nothing to pick up */ });
    return () => { alive = false; };
  }, [initialStatus]);
  const resume = Array.isArray(status?.inFlight) ? status.inFlight[0] || null : null;

  const flow = useInlinePublish({ bridge, onOpenPage, resume });
  const { stage } = flow;

  useEffect(() => { onBusyChange?.(flow.busy); }, [flow.busy, onBusyChange]);

  // The school's editable pages (same source as Content Edits: GET /api/pages,
  // scoped to the signed-in client's account). Links in the page don't
  // navigate while editing, so this is how staff move between pages.
  const [pages, setPages] = useState([]);
  useEffect(() => {
    let alive = true;
    Promise.resolve(api.get('/api/pages'))
      .then((d) => { if (alive) setPages(Array.isArray(d?.pages) ? d.pages : []); })
      .catch(() => { /* picker just lists the current page */ });
    return () => { alive = false; };
  }, []);

  // Removing the last change from the review goes back to editing.
  useEffect(() => {
    if (stage === 'review' && entries.length === 0) flow.keepEditing();
  }, [stage, entries.length, flow]);

  // Focus management: a new card is always announced; it takes focus only when
  // nothing else owns it — not while the person is typing in the page (focus
  // is in the frame) and not while the picture form is open (it hands focus
  // to the card itself when closed).
  const cardRefs = useRef(new Map());
  const knownIds = useRef(new Set());
  const imageFormOpen = !!bridge.selected && isImageTarget(bridge.selected);
  useEffect(() => {
    const fresh = entries.filter((e) => !knownIds.current.has(e.change.id));
    knownIds.current = new Set(entries.map((e) => e.change.id));
    if (!fresh.length || stage !== 'edit') return;
    const newest = fresh[0];
    setAnnouncement(`Change added: ${targetLabel(newest.change.kind === 'image' ? 'img' : newest.change.fingerprint?.tag)} on ${newest.change.path}`);
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    if ((active && active.tagName === 'IFRAME') || imageFormOpen) return;
    const el = cardRefs.current.get(newest.change.id);
    if (el) el.focus();
  }, [entries, imageFormOpen, stage]);
  const focusCard = useCallback((id) => {
    setTimeout(() => cardRefs.current.get(id)?.focus(), 0);
  }, []);

  const statusCtx = useCallback((entry) => checkStatus(entry, {
    pendingWidth: bridge.pendingMeasures.get(entry.change.id)?.width || null,
    phoneAuto: bridge.phoneAuto || 'off',
    phoneCheck: bridge.phoneChecks?.get?.(entry.change.id),
  }), [bridge.pendingMeasures, bridge.phoneAuto, bridge.phoneChecks]);

  // Look at a change at one width: its page, in the visible frame.
  const checkAt = useCallback((entry, width) => {
    if (entry.change.path !== bridge.currentPath) onOpenPage?.(entry.change.path);
    bridge.setWidthMode(width);
  }, [bridge, onOpenPage]);

  if (stage === 'review') {
    return (
      <ReviewStage
        entries={entries}
        statusOf={statusCtx}
        uploads={bridge.uploads}
        siteOrigin={siteOrigin}
        disabledReason={publishDisabledReason({
          entries,
          pending: bridge.pendingMeasures,
          phoneAuto: bridge.phoneAuto || 'off',
          phoneChecks: bridge.phoneChecks,
          uploading,
          uploads: bridge.uploads,
        })}
        onRemove={(id) => bridge.undo(id)}
        onCheck={checkAt}
        onPublish={() => flow.publish()}
        onKeepEditing={flow.keepEditing}
      />
    );
  }
  if (stage === 'progress') {
    return (
      <ProgressStage er={flow.er} accepted={flow.accepted} confirming={flow.confirm === 'checking'}
        startedAt={flow.startedAt} lostContact={flow.lostContact} />
    );
  }
  if (stage === 'success') return <SuccessStage flow={flow} siteOrigin={siteOrigin} onMakeMore={flow.makeMore} />;
  if (stage === 'under_review') return <UnderReviewStage flow={flow} siteOrigin={siteOrigin} onMakeMore={flow.makeMore} />;
  if (stage === 'failed') {
    return (
      <FailedStage
        outcome={flow.outcome}
        hasChanges={entries.length > 0}
        canRetry={entries.length > 0 && !NO_RETRY_REASONS.has(flow.outcome?.reason)}
        onRetry={() => flow.publish()}
        onKeepEditing={flow.keepEditing}
      />
    );
  }

  // ---- EDIT ----
  const disabledReason = reviewDisabledReason({ entries, uploading, uploads: bridge.uploads });
  const selected = bridge.selected;
  const showImageForm = !!selected && isImageTarget(selected);
  const showTextHint = !!selected && !isImageTarget(selected);

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="inline-edit-panel">
      <StageShell
        testId="inline-stage-edit"
        footer={(
          <>
            <button type="button" onClick={flow.toReview} disabled={!!disabledReason}
              aria-describedby={disabledReason ? 'inline-review-hint' : undefined}
              className={`${focusRing} ${primaryBtn}`}>
              {entries.length ? `Review & publish (${entries.length})` : 'Review & publish'}
            </button>
            {disabledReason && (
              <p id="inline-review-hint" className="text-[11px] text-gray-500 text-center">{disabledReason}</p>
            )}
          </>
        )}
      >
        <div>
          <h2 className="text-lg font-semibold text-white tracking-tight">Edit text &amp; images</h2>
          <p className="text-[13px] text-gray-400 leading-relaxed mt-2">
            Click any text to retype it. Click a photo to replace it. Only wording and pictures change; the design stays as it is.
          </p>
        </div>

        <PagePicker
          pages={pages}
          currentPath={bridge.currentPath}
          entries={entries}
          onOpenPage={onOpenPage}
        />

        <div role="group" aria-label="Preview width" className="grid grid-cols-2 gap-1.5">
          {[
            { mode: 'desktop', label: 'Desktop', Icon: Monitor },
            { mode: 'phone', label: 'Phone', Icon: Smartphone },
          ].map(({ mode, label, Icon }) => {
            const sel = bridge.widthMode === mode;
            return (
              <button key={mode} type="button" aria-pressed={sel} onClick={() => bridge.setWidthMode(mode)}
                className={`${focusRing} flex items-center justify-center gap-1.5 py-2 rounded-lg border text-[12px] transition-all ${sel ? 'bg-[#B0FC38]/[0.12] border-[#B0FC38]/40 text-white' : 'bg-white/[0.02] border-white/[0.06] text-gray-400 hover:border-white/[0.12] hover:text-gray-200'}`}>
                <Icon className="w-3.5 h-3.5" aria-hidden="true" /> {label}
              </button>
            );
          })}
        </div>
        {bridge.phoneAuto !== 'off' && (
          <p className="-mt-2 text-[11px] text-gray-500" data-testid="phone-auto-hint">Every change is also checked on a phone-size screen for you.</p>
        )}

        {bridge.error ? (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[12px] text-red-300/90">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />
            <span>{bridge.error}</span>
          </div>
        ) : !bridge.ready ? (
          <div className="flex items-center gap-2 text-[12px] text-gray-500">
            <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Connecting to your site…
          </div>
        ) : null}

        {bridge.lastBlocked?.reason === 'not_editable' ? (
          // Clicks on empty areas land here too: a quiet hint, not a warning.
          <p className="text-[11px] text-gray-500" data-testid="inline-blocked-hint">
            {describeBlocked('not_editable')} Click a heading, some text or a photo.
          </p>
        ) : bridge.lastBlocked && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-yellow-400/[0.06] border border-yellow-400/25 text-[12px] text-yellow-100/90" data-testid="inline-blocked">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-yellow-300" aria-hidden="true" />
            <span className="flex-1">
              {bridge.lastBlocked.message || describeBlocked(bridge.lastBlocked.reason)}{' '}
              <Link to="/portal/edits" state={{ openForm: true }} className="underline text-yellow-200 hover:text-white">Send a normal edit request</Link> for anything you can’t change here.
            </span>
            <button type="button" onClick={bridge.clearBlocked} aria-label="Dismiss" className={`${focusRing} p-0.5 rounded text-yellow-200/70 hover:text-white`}>
              <X className="w-3 h-3" aria-hidden="true" />
            </button>
          </div>
        )}

        {showTextHint && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#B0FC38]/[0.08] border border-[#B0FC38]/20 text-[12px] text-[#d9ffa0]">
            <span className="flex-1">Type the new wording in the page, then press Enter to keep it or Esc to put it back.</span>
            <button type="button" onClick={bridge.cancelEdit} className={`${focusRing} shrink-0 text-[11px] underline hover:text-white`}>Stop editing</button>
          </div>
        )}

        {showImageForm && (
          <InlineImageForm
            key={`${selected.changeId || selected.id || ''}:${selected.at}`}
            target={selected}
            siteOrigin={siteOrigin}
            bridge={bridge}
            onUploadingChange={setUploading}
            onClosed={focusCard}
          />
        )}

        <section aria-labelledby="inline-changes-heading" className="flex flex-col gap-2">
          <h3 id="inline-changes-heading" className={labelClasses}>
            {entries.length ? `Your changes (${entries.length})` : 'Your changes'}
          </h3>
          {entries.length === 0 ? (
            <p className="text-[12px] text-gray-500">No changes yet.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {entries.map((entry) => (
                <ChangeCard
                  key={entry.change.id}
                  entry={entry}
                  status={statusCtx(entry)}
                  upload={bridge.uploads.get(entry.change.id)}
                  siteOrigin={siteOrigin}
                  currentPath={bridge.currentPath}
                  headingRef={(el) => { if (el) cardRefs.current.set(entry.change.id, el); else cardRefs.current.delete(entry.change.id); }}
                  onUndo={() => bridge.undo(entry.change.id)}
                  onCheck={(width) => checkAt(entry, width)}
                />
              ))}
            </ul>
          )}
        </section>
        <p className="sr-only" aria-live="polite">{announcement}</p>
      </StageShell>
    </div>
  );
}

function PagePicker({ pages, currentPath, entries, onOpenPage }) {
  const counts = new Map();
  entries.forEach((e) => counts.set(e.change.path, (counts.get(e.change.path) || 0) + 1));
  const options = pages
    .filter((p) => p && typeof p.path === 'string')
    .map((p) => ({ path: p.path, title: p.title || '' }));
  if (currentPath && !options.some((o) => o.path === currentPath)) options.unshift({ path: currentPath, title: '' });
  const label = (o) => {
    const base = o.title ? `${o.title} — ${o.path}` : o.path;
    const n = counts.get(o.path);
    return n ? `${base} (${pluralize(n, 'change')})` : base;
  };
  return (
    <div>
      <label htmlFor="inline-page-picker" className={labelClasses}>Page</label>
      <select
        id="inline-page-picker"
        value={currentPath || ''}
        onChange={(e) => { if (e.target.value && e.target.value !== currentPath) onOpenPage?.(e.target.value); }}
        aria-describedby="inline-page-picker-hint"
        className={`${inputClasses} appearance-auto`}
      >
        {options.map((o) => <option key={o.path} value={o.path} className="bg-[#0a0a0f]">{label(o)}</option>)}
      </select>
      <p id="inline-page-picker-hint" className="mt-1 text-[11px] text-gray-500">Links don’t navigate while editing — pick a page here.</p>
    </div>
  );
}

const BADGE = {
  fits: 'bg-green-400/[0.08] border-green-400/30 text-green-300',
  grew: 'bg-yellow-400/[0.08] border-yellow-400/30 text-yellow-300',
  blocked: 'bg-red-500/[0.08] border-red-500/30 text-red-300',
  checking: 'bg-white/[0.04] border-white/[0.1] text-gray-400',
  unchecked: 'bg-white/[0.04] border-white/[0.1] text-gray-400',
};

function ChangeCard({ entry, status, upload, siteOrigin, currentPath, headingRef, onUndo, onCheck }) {
  const { change, measures, missing } = entry;
  const isImage = change.kind === 'image';
  const label = targetLabel(isImage ? 'img' : change.fingerprint?.tag);
  const details = verdictDetails(measures);
  const widths = [status.desktop, status.phone];
  let verdict;
  if (widths.includes('blocked')) verdict = 'blocked';
  else if (widths.includes('checking')) verdict = 'checking';
  else if (widths.includes('unchecked')) verdict = 'unchecked';
  else verdict = details.verdict === 'grew' ? 'grew' : 'fits';
  const headingId = `inline-card-${change.id}`;
  const soft = softWarnings(measures);

  let badgeText;
  if (verdict === 'fits') badgeText = 'Fits';
  else if (verdict === 'grew') badgeText = 'Grew';
  else if (verdict === 'blocked') badgeText = isImage ? 'Doesn’t fit' : 'Too long';
  else if (verdict === 'checking') badgeText = 'Checking…';
  else badgeText = 'Not checked yet';

  const uncheckedWidth = status.desktop === 'unchecked' ? 'desktop' : (status.phone === 'unchecked' ? 'phone' : null);
  const uncheckedLabel = uncheckedWidth === 'phone' ? 'Phone' : 'Desktop';

  return (
    <li>
      <article aria-labelledby={headingId} className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-3 flex flex-col gap-2">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <h4 id={headingId} ref={headingRef} tabIndex={-1} className={`text-[13px] font-medium text-white ${focusRing} rounded`}>
              {label}
            </h4>
            <div className="text-[11px] text-gray-500 font-mono truncate">{change.path}{change.path === currentPath ? '' : ' (another page)'}</div>
          </div>
          <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium ${BADGE[verdict]}`} data-testid="verdict-badge">
            {badgeText}
          </span>
        </div>

        {isImage ? (
          <div className="flex flex-col gap-1.5">
            <PictureSwap
              beforeSrc={absoluteSrc(change.before?.src, siteOrigin)}
              beforeLabel={`Current picture: ${imageFileName(change.before?.src)}`}
              afterSrc={upload?.dataUrl || attachmentUrl(upload?.key)}
              afterLabel={`New picture: ${upload?.filename || ''}`}
            />
            <NewDescription change={change} alt={upload?.decorative ? '' : (upload?.alt ?? change.after?.alt)} decorative={upload?.decorative ?? change.decorative} />
          </div>
        ) : (
          <div className="text-[12px] leading-relaxed">
            <p className="text-gray-500 line-through break-words"><span className="sr-only">Before: </span>{change.before?.text}</p>
            <p className="text-white break-words"><span className="sr-only">After: </span>{change.after?.text || <em className="text-gray-500">(empty)</em>}</p>
          </div>
        )}

        {verdict === 'grew' && (
          <p className="text-[11px] text-yellow-200/90">Wraps onto {pluralize(details.grewLines, 'line')} now. Check it still looks right.</p>
        )}
        {verdict === 'blocked' && (
          <div className="text-[11px] text-red-200/90 flex flex-col gap-0.5">
            {describeReasons(details.reasons).map((r) => <p key={r}>{r}</p>)}
            {details.blockedWidths.length === 1 && <p>This happens on {details.blockedWidths[0] === 'phone' ? 'phones' : 'larger screens'}.</p>}
            <p className="font-medium">{fixAdvice(change)}</p>
          </div>
        )}
        {verdict === 'checking' && status.phone === 'checking' && status.desktop !== 'checking' && (
          <p className="text-[11px] text-gray-400">Checking phones…</p>
        )}
        {verdict === 'unchecked' && uncheckedWidth && (
          <div className="flex items-center gap-2 text-[11px] text-gray-400">
            <span>Not checked on {uncheckedLabel} yet.</span>
            <button type="button" onClick={() => onCheck(uncheckedWidth)}
              className={`${focusRing} underline text-[#B0FC38] hover:text-white`}>
              Check on {uncheckedLabel}
            </button>
          </div>
        )}
        {soft.map((t) => (
          <p key={t} className="text-[11px] text-yellow-200/90 flex items-start gap-1.5" data-testid="soft-warning">
            <Info className="w-3 h-3 shrink-0 mt-0.5 text-yellow-300" aria-hidden="true" />{t}
          </p>
        ))}
        {missing && (
          <p className="text-[11px] text-yellow-200/90">We can’t find this on the page right now. It will still be sent; a NexSav team member will check it.</p>
        )}

        <div className="flex justify-end">
          <button type="button" onClick={onUndo} aria-label={`Undo ${label.toLowerCase()} change on ${change.path}`}
            className={`${focusRing} inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-white/[0.08] text-[11px] text-gray-300 hover:text-white hover:border-white/[0.2]`}>
            <Undo2 className="w-3 h-3" aria-hidden="true" /> Undo
          </button>
        </div>
      </article>
    </li>
  );
}
